// Personal Access Token scope tiers — 0.1.39.
//
// Tiers encode INTENT on a PAT row. A token minted with
// `tier: 'update'` says "this is a general-purpose automation daemon,
// give it what an update-tier daemon needs" — and if the tier's
// canonical scope set later gains a cap, the token silently picks it
// up on the next request (see resolveBearerSession's live expansion
// + snapshot union).
//
// Design constraints (see docs/proposals/0.1.39-pat-scope-tiers.md):
//
//   1. `read-only` intentionally OMITS `workitems:view` so an
//      observability-tier PAT cannot read full ticket details.
//      Collection listing uses `workitems:list` (0.1.39 split).
//   2. Every non-read-only tier includes BOTH `workitems:list` AND
//      `workitems:view` so update/create/admin daemons can hit the
//      collection route AND detail routes.
//   3. Tier bumps are retroactive — the auth path expands the tier
//      from THIS module on every request. Snapshot union in the
//      auth path prevents narrowing from silently retracting a cap
//      that a token already had (see §5.2 union-narrowing rule).
//   4. Config-file override at apps/api/config/pat-tiers.json can
//      add/remove caps from the four public tiers only. New tier
//      names via config are NOT mintable in 0.1.39 (mint schema
//      validates against PAT_TIER_NAMES) — extension deferred.
//   5. Live intersection with the user's current caps always applies.
//      A tier can never grant more than the user holds.
export const PAT_TIER_NAMES = [
    'read-only',
    'update',
    'create',
    'admin',
    'custom',
];
/**
 * Canonical scope sets for each public tier. `custom` is intentionally
 * absent — custom-tier tokens supply their own scopes at mint time.
 *
 * Editing this table changes the effective caps of every existing PAT
 * of the affected tier on their NEXT authenticated request. Removing a
 * cap does NOT revoke it from tokens that already had it via their
 * mint-time snapshot (see §5.2 union-narrowing rule); a manual
 * audit/revoke pass is required for security-motivated narrowing.
 */
export const PAT_TIER_DEFAULTS = {
    // Observability tier. Deliberately excludes `workitems:view` — an
    // read-only PAT should list projects, list work items, and read
    // comments, but NOT read full ticket details. See §2.3.
    'read-only': [
        'workitems:list',
        'projects:view',
        'comments:view',
    ],
    // General-purpose automation. Reads + writes on work items and
    // comments; transitions including review-gated. Both `workitems:list`
    // AND `workitems:view` are included so daemons hit both collection
    // and detail routes.
    'update': [
        'workitems:list', 'workitems:view',
        'projects:view', 'comments:view',
        'workitems:edit',
        'workitems:transition',
        'workitems:transition:review',
        'workitems:assign',
        'workitems:link',
        'comments:create',
        'workitems:author_override',
        'documents:view',
        'tags:view',
        'workflows:view',
    ],
    // Automation that also mints new tickets + projects. Superset of update.
    'create': [
        'workitems:list', 'workitems:view',
        'projects:view', 'comments:view',
        'workitems:edit', 'workitems:transition', 'workitems:transition:review',
        'workitems:assign', 'workitems:link',
        'comments:create',
        'workitems:author_override',
        'documents:view', 'tags:view', 'workflows:view',
        'workitems:create',
        'projects:create',
    ],
    // Orchestrator identities. Includes pats:manage (self-mint) and
    // system:admin. Only effectively mintable by users who already hold
    // those caps — the intersection rule bounds it.
    'admin': [
        'workitems:list', 'workitems:view',
        'projects:view', 'comments:view',
        'workitems:edit', 'workitems:transition', 'workitems:transition:review',
        'workitems:assign', 'workitems:link',
        'comments:create',
        'workitems:author_override',
        'documents:view', 'tags:view', 'workflows:view',
        'workitems:create', 'projects:create',
        'pats:manage',
        'system:admin',
    ],
};
/** Human-readable descriptions for UI + docs. */
export const PAT_TIER_DESCRIPTIONS = {
    'read-only': 'Observability sessions, dashboard builders, audit runs.',
    'update': 'Most sessions — mark tickets done, add comments, patch fields.',
    'create': 'Sessions that mint new tickets. Rare — most CREATE routes through COO refresh pipeline.',
    'admin': 'COO sessions, orchestrators, script daemons. Includes pats:manage and system:admin.',
    'custom': 'Hand-picked scopes. Discouraged for shared integrations; audit trail lives on the token row.',
};
/**
 * Caps that trigger a WARN-level log when added to a lower-privilege
 * tier via `apps/api/config/pat-tiers.json` override. Config-widening
 * a tier to include these is a deliberate choice, not silently allowed
 * to slip past ops review. See §5.2 loud-log approach.
 *
 * Not a hard gate — the loud log is proportionate to a single-caller
 * deployment. A hard-panic env-var gate is deferred (§6.3) until
 * multi-operator deployments make it necessary.
 */
export const PAT_TIER_HIGH_RISK_CAPS = [
    'system:admin',
    'pats:manage',
];
/**
 * Return true if `tier` is one of the four public tiers (i.e., not
 * `custom` and not unknown). Convenience for loader validation +
 * mint-side branching.
 */
export function isPublicTier(tier) {
    return (tier === 'read-only' ||
        tier === 'update' ||
        tier === 'create' ||
        tier === 'admin');
}
//# sourceMappingURL=pat-tiers.js.map